import { ArrowUpRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { PricingCard } from './PricingCard'
import { useState } from 'react'
import { useTranslation } from '@/hooks/useTranslation'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { PricingFeatureItem } from './PricingFeatureItem'
import {
	getPlanFeatures,
	usePricingData,
	buildDefaultPlans,
	mergePricingData,
	calculateSavings,
} from '@/lib/pricing'
import { BillingToggle } from '@/components/settings/PricingCard'
import { BRAND_MENTION_ADDON_PRICE_MONTHLY } from '@/types/brandMentions'

export function Pricing() {
	const { t } = useTranslation()
	const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>(
		'yearly'
	)
	const [brandMentionsAddon, setBrandMentionsAddon] = useState(false)
	const planFeatures = getPlanFeatures(t)

	const { data: pricingData, isLoading } = usePricingData()

	const defaultPlans = buildDefaultPlans(t, planFeatures)
	const plans = mergePricingData(defaultPlans, pricingData)

	const savingsPercentage = calculateSavings(
		typeof plans.starter.monthlyPrice === 'number'
			? plans.starter.monthlyPrice
			: 0,
		typeof plans.starter.yearlyPrice === 'number'
			? plans.starter.yearlyPrice
			: 0
	)

	const addonMonthly = brandMentionsAddon
		? BRAND_MENTION_ADDON_PRICE_MONTHLY
		: 0
	const addonYearly = addonMonthly * 12

	const brandMentionsAddonToggle = {
		checked: brandMentionsAddon,
		onCheckedChange: setBrandMentionsAddon,
		title: t('billingSettings.brandMentionsAddonTitle'),
		priceLabel: t('pricing.addons.brandMentionsPrice'),
		description: t('billingSettings.brandMentionsAddonDescription'),
	}

	const calculatePlanPrice = (planKey: 'starter' | 'growth') => {
		const plan = plans[planKey]
		const baseMonthlyPrice =
			typeof plan.monthlyPrice === 'number' ? plan.monthlyPrice : 0
		const baseYearlyPrice =
			typeof plan.yearlyPrice === 'number' ? plan.yearlyPrice : 0

		if (billingPeriod === 'monthly') {
			return baseMonthlyPrice + addonMonthly
		}

		return Math.round(baseYearlyPrice / 12) + addonMonthly
	}

	const calculateTotalYearlyPrice = (planKey: 'starter' | 'growth') => {
		const plan = plans[planKey]
		const baseYearly =
			typeof plan.yearlyPrice === 'number' ? plan.yearlyPrice : 0
		return baseYearly + addonYearly
	}

	return (
		<section id="pricing" className="py-20 sm:py-24 lg:py-28">
				<div className="container mx-auto">
				<div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
					<div>
						<p className="text-xs font-semibold uppercase tracking-[.2em] text-[#6547D9]">
							{t('pricing.label')}
						</p>
					<h2 className="mt-4 max-w-2xl font-heading text-4xl font-semibold tracking-[-.04em] text-[#17131F] sm:text-5xl">
						{t('pricing.title')}
					</h2>
				</div>
					<BillingToggle
						billingPeriod={billingPeriod}
						onChange={setBillingPeriod}
						savingsPercentage={savingsPercentage}
					/>
				</div>

				<div className="mt-12 grid w-full overflow-hidden rounded-[28px] border border-[#D9D2E7] bg-white lg:grid-cols-3">
					<PricingCard
						name={plans.free.name}
						isMostPopular={false}
						features={plans.free.features}
						price={0}
						isLoading={isLoading}
						className="rounded-none border-0 border-b border-[#E7E2EF] lg:border-b-0 lg:border-r"
					/>
					<PricingCard
						name={plans.starter.name}
						isMostPopular={false}
						features={plans.starter.features}
						price={calculatePlanPrice('starter')}
						isLoading={isLoading}
						className="rounded-none border-0 border-b border-[#E7E2EF] lg:border-b-0 lg:border-r"
						priceSubtext={
							billingPeriod === 'yearly'
								? t('pricingCard.billedYearly', {
										price: calculateTotalYearlyPrice('starter'),
								  })
								: undefined
						}
						addonToggle={{
							...brandMentionsAddonToggle,
							id: 'landing-starter-brand-mentions-addon',
						}}
						customButton={
							<Button
								size="lg"
								asChild
								className="bg-[#6547D9] py-4 text-lg font-semibold text-white hover:bg-[#5638C7]"
							>
								<Link to="/auth?redirect=pricing" data-price-id="starter">
									{t('pricingCard.getStarted')}
								</Link>
							</Button>
						}
					/>
					<PricingCard
						name={plans.growth.name}
						isMostPopular={true}
						features={plans.growth.features}
						price={calculatePlanPrice('growth')}
						isLoading={isLoading}
						className="rounded-none border-0 bg-[#F5F1FF]"
						priceSubtext={
							billingPeriod === 'yearly'
								? t('pricingCard.billedYearly', {
										price: calculateTotalYearlyPrice('growth'),
								  })
								: undefined
						}
						addonToggle={{
							...brandMentionsAddonToggle,
							id: 'landing-growth-brand-mentions-addon',
						}}
						customButton={
							<Button
								size="lg"
								asChild
								className="bg-[#6547D9] py-4 text-lg font-semibold text-white hover:bg-[#5638C7]"
								>
								<Link to="/auth?redirect=pricing" data-price-id="growth">
									{t('pricingCard.getGrowth')}
								</Link>
								</Button>
							}
					/>
				</div>

				<Card className="fb-enterprise mt-5 w-full shadow-none">
					<div className="fb-enterprise-layout">
						<div className="fb-enterprise-content space-y-5">
							<div className="space-y-2">
								<p className="fb-eyebrow">Enterprise</p>
								<h3 className="font-heading text-3xl font-semibold text-foreground">
									{t('pricing.enterprise.ctaTitle')}
								</h3>
								<p className="text-lg text-muted-foreground max-w-2xl">
									{t('pricing.enterprise.description')}
								</p>
							</div>
							<ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
								{plans.enterprise.features.map((feature, index) => (
									<PricingFeatureItem key={index} text={feature} />
								))}
							</ul>
						</div>
						<Button size="lg" variant="outline" className="fb-enterprise-cta font-semibold text-lg py-4" asChild>
							<a href="mailto:contact@feedbask.com">
								{t('pricingCard.contactUs')} <ArrowUpRight size={16} />
							</a>
						</Button>
					</div>
				</Card>
			</div>
		</section>
	)
}
